import { useCallback, useEffect, useState } from "react";
import { Routes } from "../../navigation";
import { transformRoute, useTranslate } from "helpers";
import { ContactSearchPageCmp, ContactSearchPageProps } from "./types";
import { Breadcrumb } from "ui/components/Breadcrumb";
import { PageHeader } from "ui/components/PageHeader";
import { PageTitle } from "ui/components/PageTitle";
import { DataWithPagination } from "services/api";
import { initialDataWithPagination } from "services/api/consts";
import { Pagination } from "services/search";
import { useSelectedItemsChanges } from "ui/components/Table/hooks";
import { NoResults } from "ui/components/NoResults";
import { Routes as ContactRoutes } from "ui/modules/contacts/navigation";
import { useNavigate } from "react-router-dom";
import { ContactRow, ContactSearchFormValues } from "services/api/contacts/types";
import { CONTACT_SEARCH_PAGE_TITLE } from "ui/components/PageTitle/consts";
import { deleteContactAPI, getContactsAPI } from "services/api/contacts/api";
import ContactSearchFilters from "./components/ContactSearchFilters/ContactSearchFilters";
import ContactSearchResults from "./components/ContactSearchResults/ContactSearchResults";
import ContactEditCard from "../ContactDetailPage/components/ContactDetailCard/components/ContactEditCard/ContactEditCard";
import { Button } from "ui/components/Button/Button";
import ContactImportCard from "../components/ContactImportCard/ContactImportCard";
import ContactExportCard from "../components/ContactExportCard/ContactExportCard";
import { Dialog } from "ui/components/Dialog";

const ContactSearchPage: ContactSearchPageCmp = (props: ContactSearchPageProps) => {

  const { t } = useTranslate();

  const [searchResults, setSearchResults] = useState<
    DataWithPagination<ContactRow>
  >(initialDataWithPagination);

  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [contactId, setContactId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showAddNewModal, setShowAddNewModal] = useState<boolean>(false);
  const [showImportModal, setShowImportModal] = useState<boolean>(false);
  const [showExportModal, setShowExportModal] = useState<boolean>(false);
  const [openDialog, setOpenDialog] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    fetchSearchResult(searchResults.pagination);
  }, []);

  const fetchSearchResult = async (
    pagination: Pagination,
    name?: string | null,
    contactTypeId?: string | null,
    contactGroupIds?: string[] | null,
    email?: string | null,
    phone?: string | null,
    address?: string | null,
  ) => {
    setIsLoading(true);
    try {

      const results = await getContactsAPI(pagination, name, contactTypeId, contactGroupIds, email, phone, address);

      setSearchResults(results);
    } catch {
      setSearchResults(initialDataWithPagination);
      setIsLoading(false);
      return;
    }
    setIsLoading(false);
  };

  const handleRowClick = useCallback(async (id: string) => {
    navigate(
      transformRoute(ContactRoutes.ContactDetailPageRouter, {
        id,
      })
    );
  }, []);

  const handleAddNew = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      setContactId(null);
      setShowAddNewModal(true);
    },
    []
  );

  const handleSearchChange = (searchFormValues: ContactSearchFormValues) => {
    fetchSearchResult(searchResults.pagination, searchFormValues.search, searchFormValues.contactTypeId, searchFormValues.contactGroupIds, searchFormValues.email, searchFormValues.phone, searchFormValues.address);
  };

  const handleEditSuccess = () => {
    fetchSearchResult(searchResults.pagination);
    setContactId(null);
    setShowAddNewModal(false);
  };


  const handleSelectedChange = useSelectedItemsChanges(
    selectedRows,
    setSelectedRows
  );

  const handlePaginationChange = (newPagination: Pagination) => {
    fetchSearchResult(newPagination);
  };

  const handlePhoneClick = useCallback((event: React.MouseEvent<HTMLSpanElement>, phone: string) => {
    event.stopPropagation();
    event.preventDefault();
    window.location.href = `tel:${phone}`;
  }, []);

  const handleEmailClick = (event: React.MouseEvent<HTMLSpanElement, MouseEvent>, emailAddress: string) => {
    event.stopPropagation();
    event.preventDefault();
    window.location.href = `mailto:${emailAddress}`;
  }

  const handleGoogleMapClick = useCallback(
    (
      event: React.MouseEvent<HTMLSpanElement>,
      latitude: number | null,
      longitude: number | null
    ) => {
      event.stopPropagation();
      event.preventDefault();
      const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}`
      window.open(googleMapsUrl, "_blank", "noopener,noreferrer");
    },
    []
  );

  const handleDeleteClick = (event: React.MouseEvent<HTMLSpanElement, MouseEvent>, id: string) => {
    event.stopPropagation();
    event.preventDefault();

    setContactId(id);
    setOpenDialog(true);
  }

  const handleDeleteDialogConfirm = useCallback(async () => {
    if (!contactId) return;

    try {
      const deleted = await deleteContactAPI(
        t,
        contactId,
      );

      if (deleted && contactId) {
        await fetchSearchResult(searchResults.pagination);
      }

      // zatvori dijalog nakon brisanja
      setOpenDialog(false);
      setContactId("");
    } catch (err) {
      console.error("Greška prilikom brisanja kontakta:", err);
    }
  }, [contactId, t, contactId, searchResults.pagination]);

  const handleDeleteDialogClose = useCallback(() => {
    setContactId("");
    setOpenDialog(false);
  }, []);




  return (
    <>
      <div className="">
        <div className="bg-white rounded-t-md border border-b mb-2">
          <PageHeader>
            <div className="flex items-center justify-between">
              <div>
                <PageTitle
                  title={CONTACT_SEARCH_PAGE_TITLE.text}
                  icon={CONTACT_SEARCH_PAGE_TITLE.icon}
                  iconColor={CONTACT_SEARCH_PAGE_TITLE.iconColor}
                />
                <Breadcrumb />
              </div>
              <div className="flex items-center gap-2">
                {/* <ButtonMenu
                text="Grupne akcije"
                cssClassName="h-full"
                variant="secondary"
                size="large"
                startIcon="/media/icons/plus.svg"
              >
                <ButtonMenuItem
                  startIcon="/media/icons/document-upload.svg"
                  onClick={() => setShowImportModal(true)}
                >
                  Import kontakata
                </ButtonMenuItem>
                <ButtonMenuItem
                  startIcon="/media/icons/document-download.svg"
                  onClick={() => setShowExportModal(true)}
                >
                  Export kontakata
                </ButtonMenuItem>
              </ButtonMenu> */}

                <Button
                  variant="primary"
                  children="Kreiraj kontakt"
                  startIcon="/media/icons/plus.svg"
                  onClick={handleAddNew}
                />
              </div>
            </div>

          </PageHeader>
        </div>
        <div className="px-0 pt-2 mx-[-1px] xl:px-6">
          <ContactSearchFilters
            onAddNew={handleAddNew}
            onSearchChange={handleSearchChange}
          />
          {!isLoading && searchResults.pagination.totalPages === 0 ? (
            <NoResults message="Unesite kontakt klikom na 'Dodaj kontakt'" />
          ) : (
            <ContactSearchResults
              searchResults={searchResults}
              selectedRows={selectedRows}
              isLoading={isLoading}
              onRowClick={handleRowClick}
              onPaginationChange={handlePaginationChange}
              onSelectedChange={handleSelectedChange}
              onDeleteClick={handleDeleteClick}
              onEmailClick={handleEmailClick}
              onPhoneClick={handlePhoneClick}
              onGoogleMapClick={handleGoogleMapClick}
            />
          )}
        </div>
      </div>
      <ContactEditCard
        contactId={contactId}
        show={showAddNewModal}
        onSuccess={handleEditSuccess}
        onClose={() => setShowAddNewModal(false)}
      />
      <Dialog
        open={openDialog}
        title={t(
          "admin.contact.search.dialog.delete.title"
        )}
        message={t(
          "admin.contact.search.dialog.delete.message"
        )}
        confirmText={t(
          "admin.contact.search.dialog.delete.confirmtext"
        )}
        cancelText={t(
          "admin.contact.search.dialog.delete.canceltext"
        )}
        onConfirm={handleDeleteDialogConfirm}
        onCancel={handleDeleteDialogClose}
      />
      <ContactImportCard
        show={showImportModal}
        onClose={() => setShowImportModal(false)}
      />
      <ContactExportCard
        show={showExportModal}
        onClose={() => setShowExportModal(false)}
      />
    </>
  );
};

ContactSearchPage.route = Routes.ContactSearchPageRouter;

export default ContactSearchPage;
